<script setup lang="ts">
import PromosBlock from './cart-amount-blocks/PromosBlock.vue'
import SubtotalsBlock from './cart-amount-blocks/SubtotalsBlock.vue'
import FeesBlock from './cart-amount-blocks/FeesBlock.vue'
import TotalBlock from './cart-amount-blocks/TotalBlock.vue'
import {
  CartTotals,
  PromosBlockType,
  SubtotalsBlockType,
  FeesBlockType,
  TotalBlockType,
} from '~/lib/services/store/catalog/cart.dtos'
import { StoreOrder } from '~/lib/services/store/order/order.dto'

const props = withDefaults(
  defineProps<{
    order?: StoreOrder
    prices: CartTotals
    hidden?: string[]
    tax: boolean
    promotionStr?: string
    freeShippingPromo?: boolean
    showShipping?: boolean
    serviceFeeName: string | undefined
  }>(),
  {
    order: undefined,
    hidden: undefined,
    promotionStr: undefined,
    freeShippingPromo: false,
    showShipping: false,
    serviceFeeName: undefined,
  }
)

const { areaSelected } = usePromotions()

type CartTotalKey = keyof CartTotals
const keysOrder: CartTotalKey[] = [
  'discount',
  'subtotal',
  'shipping',
  'serviceFee',
  'tax',
  'tip',
  'total',
]

const showedEntries = computed(() => {
  const prices = new Map<CartTotalKey, number>()
  for (const key of keysOrder) {
    let amount = 0
    let hidden = true
    if (!props.hidden || !props.hidden.length) {
      amount = props.prices[key]
      hidden = false
    } else if (!props.hidden?.includes(key)) {
      amount = props.prices[key]
      hidden = false
    }

    // if amount return this row
    if (amount) prices.set(key, amount)
    else if (!hidden && key === 'total') prices.set(key, amount)
    else if (key === 'tax' && props.tax) prices.set(key, amount)
    else if (key === 'shipping' && mustShowShipping.value) prices.set(key, amount)
  }
  return prices
})

const priceBlocks = computed(() => {
  const promosBlock = {} as PromosBlockType
  const subtotalBlock = {} as SubtotalsBlockType
  const feesBlock = {} as FeesBlockType
  const totalBlock = {} as TotalBlockType

  for (const [key, value] of showedEntries.value) {
    if (key === 'discount') promosBlock[key] = value
    else if (key === 'total') totalBlock[key] = value
    else if (key === 'subtotal' || key === 'shipping') subtotalBlock[key] = value
    else feesBlock[key] = value
  }

  return {
    promosBlock,
    subtotalBlock,
    feesBlock,
    totalBlock,
  }
})

const mustShowShipping = computed(
  () =>
    props.showShipping || props.order?.type === 'DLV' || (!props.order && areaSelected === 'DLV')
)

const blockHasEntries = (block: {}) => Object.entries(block).length
</script>

<template>
  <section class="w-full">
    <div class="my-6 flex flex-col gap-6 md:mx-4">
      <PromosBlock
        v-if="blockHasEntries(priceBlocks.promosBlock)"
        :promos-block="priceBlocks.promosBlock"
        :promotion-str="promotionStr"
      />
      <SubtotalsBlock
        v-if="blockHasEntries(priceBlocks.subtotalBlock)"
        :subtotal-block="priceBlocks.subtotalBlock"
        :free-shipping-promo="freeShippingPromo"
      />
      <FeesBlock
        v-if="blockHasEntries(priceBlocks.feesBlock)"
        :fees-block="priceBlocks.feesBlock"
        :service-fee="serviceFeeName"
      />
      <TotalBlock
        v-if="blockHasEntries(priceBlocks.totalBlock)"
        :total-block="priceBlocks.totalBlock"
      />
    </div>
  </section>
</template>
